import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Edit, History, UserPlus, Trash2, AlertCircle } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

const InstrumentTable = ({ 
  instruments, 
  onEdit, 
  onDelete, 
  onHistory, 
  onAssign,
  selectedInstruments = [],
  onSelectInstrument,
  selectAll,
  onToggleSelectAll
}) => {
  const getStatusBadge = (status) => {
    if (status === 'vencido') {
      return (
        <Badge className="bg-red-500 hover:bg-red-600 text-white border-0">
          <AlertCircle className="w-3 h-3 mr-1" />
          Vencida
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-500 hover:bg-green-600 text-white border-0">
        Em Dia
      </Badge>
    );
  };

  const getStatusInstrumentoBadge = (statusInstrumento) => {
    const statusConfig = {
      'Estoque': 'bg-black hover:bg-slate-800',
      'Emprestado': 'bg-blue-500 hover:bg-blue-600',
      'Em manutenção': 'bg-orange-500 hover:bg-orange-600',
      'Em calibração': 'bg-purple-500 hover:bg-purple-600',
    };
    
    return (
      <Badge className={`${statusConfig[statusInstrumento] || 'bg-slate-500'} text-white border-0`}>
        {statusInstrumento || 'N/A'}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getDaysUntilExpiry = (dateString) => {
    const today = new Date();
    const expiryDate = new Date(dateString);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-orange-200 overflow-hidden">
      <div className="p-6 border-b border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
        <h2 className="text-xl font-bold text-black">Todos os Instrumentos</h2>
        <p className="text-sm text-slate-700 mt-1">
          Gerencie seu inventário de instrumentos e acompanhe seus status
        </p>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-orange-50 hover:bg-orange-50 border-b border-orange-200">
              <TableHead className="font-semibold text-black w-12">
                <div className="flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={onToggleSelectAll}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                  />
                </div>
              </TableHead>
              <TableHead className="font-semibold text-black">Nome do Instrumento</TableHead>
              <TableHead className="font-semibold text-black">Marca</TableHead>
              <TableHead className="font-semibold text-black">Tipo</TableHead>
              <TableHead className="font-semibold text-black">Faixa de Medição</TableHead>
              <TableHead className="font-semibold text-black">Status Instrumento</TableHead>
              <TableHead className="font-semibold text-black">Status Calibração</TableHead>
              <TableHead className="font-semibold text-black">Vencimento</TableHead>
              <TableHead className="font-semibold text-black">Responsável</TableHead>
              <TableHead className="font-semibold text-black text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {instruments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-12 text-slate-500">
                  Nenhum instrumento encontrado
                </TableCell>
              </TableRow>
            ) : (
              instruments.map((instrument) => {
                const daysUntilExpiry = getDaysUntilExpiry(instrument.proximaCalibracao);
                const isSelected = selectedInstruments.includes(instrument.id);
                return (
                  <TableRow 
                    key={instrument.id} 
                    className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-orange-50' : ''}`}
                  >
                    <TableCell className="w-12">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onSelectInstrument(instrument.id)}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      {instrument.nome}
                    </TableCell>
                    <TableCell className="text-slate-700">{instrument.marca}</TableCell>
                    <TableCell className="text-slate-700">{instrument.tipo}</TableCell>
                    <TableCell className="text-slate-700">{instrument.faixaMedicao || 'N/A'}</TableCell>
                    <TableCell>{getStatusInstrumentoBadge(instrument.statusInstrumento)}</TableCell>
                    <TableCell>{getStatusBadge(instrument.status)}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-slate-900">
                          Vencimento: {formatDate(instrument.proximaCalibracao)}
                        </div>
                        {daysUntilExpiry < 30 && daysUntilExpiry > 0 && (
                          <div className="text-xs text-orange-600 font-medium">
                            Vence em {daysUntilExpiry} dias
                          </div>
                        )}
                        {daysUntilExpiry < 0 && (
                          <div className="text-xs text-red-600 font-medium">
                            Vencido há {Math.abs(daysUntilExpiry)} dias
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-700">{instrument.responsavel}</TableCell>
                    <TableCell>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(instrument)}
                          className="hover:bg-orange-50 hover:text-orange-600 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onHistory(instrument)}
                          className="hover:bg-amber-50 hover:text-amber-600 transition-colors"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onAssign(instrument)}
                          className="hover:bg-yellow-50 hover:text-yellow-600 transition-colors"
                        >
                          <UserPlus className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="hover:bg-red-50 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir o instrumento "{instrument.nome}"?
                                Esta ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(instrument.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default InstrumentTable;